import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

interface PDFPasswordHandlerState {
  hasError: boolean;
  passwordCancelled: boolean;
  documentKey: number;
}

interface PDFPasswordHandlerActions {
  handlePassword: (
    callback: (password: string) => void,
    reason: number
  ) => void;
  handleRetryPassword: () => void;
  handleDocumentLoadSuccess: () => void;
  handleDocumentLoadError: (error: any) => void;
  resetOnUrlChange: () => void;
  resetStatesOnly: () => void;
}

interface PDFPasswordHandlerReturn {
  state: PDFPasswordHandlerState;
  actions: PDFPasswordHandlerActions;
}

export const usePDFPasswordHandler = (
  onDocumentLoadSuccess?: () => void,
  onDocumentLoadError?: (error: any) => void
): PDFPasswordHandlerReturn => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [passwordCancelled, setPasswordCancelled] = useState<boolean>(false);
  const [documentKey, setDocumentKey] = useState<number>(0);

  const successCallbackRef = useRef(onDocumentLoadSuccess);
  const errorCallbackRef = useRef(onDocumentLoadError);

  useEffect(() => {
    successCallbackRef.current = onDocumentLoadSuccess;
  }, [onDocumentLoadSuccess]);

  useEffect(() => {
    errorCallbackRef.current = onDocumentLoadError;
  }, [onDocumentLoadError]);

  const handlePassword = useCallback(
    (callback: (password: string) => void, reason: number) => {
      setPasswordCancelled(false);

      function callbackProxy(password: string | null) {
        // User cancelled the password prompt
        if (password === null) {
          setPasswordCancelled(true);
          setHasError(true);
          return;
        }
        callback(password);
      }

      switch (reason) {
        case 1: {
          // PasswordResponses.NEED_PASSWORD
          const password = prompt(
            'This PDF is password protected. Please enter the password:'
          );
          callbackProxy(password);
          break;
        }
        case 2: {
          // PasswordResponses.INCORRECT_PASSWORD
          const retryPassword = prompt('Incorrect password. Please try again:');
          callbackProxy(retryPassword);
          break;
        }
        default:
          break;
      }
    },
    []
  );

  const handleRetryPassword = useCallback(() => {
    setPasswordCancelled(false);
    setHasError(false);
    // Force re-render of Document component by updating the key
    setDocumentKey((prev) => prev + 1);
  }, []);

  const handleDocumentLoadSuccess = useCallback(() => {
    setHasError(false);
    setPasswordCancelled(false);
    successCallbackRef.current?.();
  }, []);

  const handleDocumentLoadError = useCallback((error: any) => {
    setHasError(true);
    errorCallbackRef.current?.(error);
  }, []);

  const resetOnUrlChange = useCallback(() => {
    setHasError(false);
    setPasswordCancelled(false);
    setDocumentKey((prev) => prev + 1);
  }, []);

  const resetStatesOnly = useCallback(() => {
    setHasError(false);
    setPasswordCancelled(false);
    // Don't increment documentKey for normal URL changes
  }, []);

  const actions = useMemo(
    () => ({
      handlePassword,
      handleRetryPassword,
      handleDocumentLoadSuccess,
      handleDocumentLoadError,
      resetOnUrlChange,
      resetStatesOnly
    }),
    [
      handlePassword,
      handleRetryPassword,
      handleDocumentLoadSuccess,
      handleDocumentLoadError,
      resetOnUrlChange,
      resetStatesOnly
    ]
  );

  return {
    state: {
      hasError,
      passwordCancelled,
      documentKey
    },
    actions
  };
};
